import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { kolApplications } from "@/lib/api/kol-applications";
import type {
  KolApplication,
  KolApplicationInput,
} from "@/lib/api/kol-applications";
import { kolDashboard as api } from "@/lib/api/kol-dashboard";
import type { KolDashboard } from "@/lib/api/kol-dashboard";
import { ApiError } from "@/lib/api/transport";
import { useAuth } from "./useAuth";
import { useUserChannel } from "./useUserChannel";

export type {
  KolApplication,
  KolApplicationInput,
} from "@/lib/api/kol-applications";
type KolDashboardStatus = "idle" | "loading" | "ready" | "error";

interface KolDashboardContextValue {
  status: KolDashboardStatus;
  dashboard: KolDashboard | null;
  application: KolApplication | null;
  error: Error | null;
  reload: () => Promise<void>;
  submitApplication: (input: KolApplicationInput) => Promise<void>;
}

const KolDashboardContext = createContext<KolDashboardContextValue>(null!);

export function useKolDashboard() {
  return useContext(KolDashboardContext);
}

export function KolDashboardProvider({ children }: { children: ReactNode }) {
  const value = useKolDashboardProvider();
  return (
    <KolDashboardContext.Provider value={value}>
      {children}
    </KolDashboardContext.Provider>
  );
}

function useKolDashboardProvider(): KolDashboardContextValue {
  const { status: authStatus, refresh } = useAuth();
  const { subscribe } = useUserChannel();
  const [status, setStatus] = useState<KolDashboardStatus>("idle");
  const [dashboard, setDashboard] = useState<KolDashboard | null>(null);
  const [application, setApplication] = useState<KolApplication | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    try {
      const [next, result] = await Promise.all([
        api.getKolDashboard().catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
        kolApplications.get(),
      ]);
      setDashboard(next);
      setApplication(result.application);
      setError(null);
      setStatus("ready");
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
      setStatus("error");
    }
  }, []);

  const submitApplication = useCallback(async (input: KolApplicationInput) => {
    const result = await kolApplications.submit(input);
    setApplication(result.application);
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load remote application/dashboard after authentication
    void reload();
    const onFocus = () => void reload();
    window.addEventListener("focus", onFocus);
    const unsubscribe = subscribe((message) => {
      if (message.event !== "kol.application_changed") return;
      void reload();
      void refresh();
    });
    return () => {
      window.removeEventListener("focus", onFocus);
      unsubscribe();
    };
  }, [authStatus, reload, subscribe, refresh]);

  return useMemo(
    () => ({
      status,
      dashboard,
      application,
      error,
      reload,
      submitApplication,
    }),
    [status, dashboard, application, error, reload, submitApplication],
  );
}
